
import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import ProductRating from './ProductRating';
import { Card, CardContent } from '@/components/ui/card';

interface Review {
  id: string;
  name: string;
  rating: number;
  date: string;
  comment: string;
  avatar?: string;
}

interface ProductReviewsProps {
  productId: string;
  productName: string;
}

// Generate pseudo-random reviews based on productId for demo purposes
const generateReviews = (productId: string, productName: string): Review[] => {
  const reviewCount = 3 + (parseInt(productId, 36) % 5); // 3-7 reviews to show
  const names = [
    'John D.', 'Sarah M.', 'Michael K.', 'Emma T.', 'David W.', 
    'Lisa P.', 'Robert S.', 'Olivia R.', 'James L.', 'Sophia B.'
  ];
  const comments = [
    `Absolutely love this ${productName}! Exactly what I was looking for.`,
    `Great quality and fast delivery. Would recommend.`,
    `The ${productName} exceeded my expectations. Very satisfied with my purchase.`,
    `Good product but shipping took longer than expected.`,
    `Perfect fit and looks even better in person than in the pictures.`,
    `This ${productName} is amazing! Will definitely buy again.`,
    `Decent product for the price, but there are a few minor issues.`,
    `Very happy with my purchase. The ${productName} is well made and durable.`,
    `Excellent customer service and the product is top-notch!`,
    `The ${productName} works great but the color is slightly different than shown.`
  ];
  
  const reviews: Review[] = [];
  const today = new Date();
  
  for (let i = 0; i < reviewCount; i++) {
    // Generate deterministic but seemingly random values based on productId and index
    const charSum = (productId + i).split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
    const nameIndex = charSum % names.length;
    const commentIndex = (charSum + i) % comments.length;
    const daysAgo = (charSum % 60) + 1; // 1-60 days ago
    
    const date = new Date(today);
    date.setDate(date.getDate() - daysAgo);
    
    // Generate a rating that looks random but is centered around the product's overall rating
    const baseValue = 3 + ((charSum + i) % 20) / 10; // 3.0 to 5.0
    const variance = ((i * charSum) % 10) / 10 - 0.5; // -0.5 to 0.5
    const rating = Math.max(1, Math.min(5, baseValue + variance));
    
    reviews.push({
      id: `review-${productId}-${i}`,
      name: names[nameIndex],
      rating: Math.round(rating * 10) / 10, // Round to 1 decimal place
      date: date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
      comment: comments[commentIndex],
      avatar: `https://i.pravatar.cc/150?u=${productId}${i}`, // Random avatar based on productId and index
    });
  }
  
  return reviews;
};

const ProductReviews: React.FC<ProductReviewsProps> = ({ productId, productName }) => {
  const reviews = generateReviews(productId, productName);
  
  // Calculate average rating
  const avgRating = reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length;
  
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Customer Reviews</h2>
      
      <div className="flex items-center gap-4 mb-6">
        <div className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg">
          <span className="text-3xl font-bold">{avgRating.toFixed(1)}</span>
          <ProductRating rating={avgRating} size={20} />
          <span className="text-sm text-gray-500 mt-1">{reviews.length} reviews</span>
        </div>
        
        <div className="flex-1">
          <p className="text-gray-700 mb-2">Rating breakdown</p>
          {[5, 4, 3, 2, 1].map(stars => {
            const count = reviews.filter(r => Math.floor(r.rating) === stars).length;
            const percentage = (count / reviews.length) * 100;
            
            return (
              <div key={`stars-${stars}`} className="flex items-center gap-2 mb-1">
                <span className="text-sm text-gray-600 w-8">{stars} star</span>
                <div className="h-2 bg-gray-200 flex-1 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-yellow-400 rounded-full" 
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <span className="text-xs text-gray-500 w-8">{count}</span>
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="space-y-4">
        {reviews.map(review => (
          <Card key={review.id} className="overflow-hidden">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarImage src={review.avatar} alt={review.name} />
                    <AvatarFallback>{review.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <h4 className="font-medium">{review.name}</h4>
                    <div className="flex items-center gap-2">
                      <ProductRating rating={review.rating} size={14} />
                      <span className="text-xs text-gray-500">• {review.date}</span>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-gray-700">{review.comment}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ProductReviews;
